
import React from 'react';
import { 
Box, 
TextField, 
FormControl, 
InputLabel, 
Select, 
MenuItem, 
FormControlLabel, 
Checkbox, 
Radio, 
RadioGroup, 
Typography, 
Button 
} from '@mui/material';

export function FormFieldPreview({ field, onUpdate }) {
    const formatFileSize = (bytes) => {
        if (!bytes) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };
    
    switch (field.type) {
        case 'text':
        case 'email':
        case 'number':
            return (
                <TextField
                    label={field.label}
                    type={field.type}
                    required={field.required}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                />
            );
        
        case 'select':
            return (
                <FormControl fullWidth margin="normal" required={field.required}>
                    <InputLabel>{field.label}</InputLabel>
                    <Select label={field.label}>
                        {field.options?.map((option, index) => (
                            <MenuItem key={index} value={option}>{option}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        
        case 'label':
            return (
                <Typography 
                    variant={field.variant || 'h6'} 
                    sx={{ 
                        mt: 2, 
                        mb: 1,
                        ...(field.sx || {})
                    }}
                >
                    {field.label}
                </Typography>
            );
        
        case 'checkbox':
            return (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={field.checked || false}
                            onChange={(e) => onUpdate(field.id, { 
                                checked: e.target.checked,
                                groupName: field.groupName 
                            })}
                            name={field.groupName || field.id}
                        />
                    }
                    label={field.label}
                />
            );
        
        case 'radio':
            return (
                <FormControl component="fieldset" margin="normal">
                    <Typography variant="subtitle1">{field.label}</Typography>
                    <RadioGroup
                        value={field.value}
                        onChange={(e) => onUpdate(field.id, { value: e.target.value })}
                        name={field.groupName || field.id}
                    >
                        {field.options?.map((option, index) => (
                            <FormControlLabel 
                                key={index} 
                                value={option} 
                                control={<Radio />} 
                                label={option} 
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            );
        
        // case 'file':
        //     return (
        //         <Box margin="normal">
        //             <Typography variant="subtitle1">{field.label}</Typography>
        //             <Button variant="outlined" component="label">
        //                 Upload File
        //                 <input type="file" hidden />
        //             </Button>
        //         </Box>
        //     );        case 'file':
            return (
                <Box margin="normal">
                    <Typography variant="subtitle1">
                        {field.label}
                        {field.required && <span style={{ color: 'red' }}> *</span>}
                    </Typography>
                    
                    {/* Display file configuration info */}
                    {field.allowedFileTypes && field.allowedFileTypes !== 'all' && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Allowed: {field.allowedFileTypes === 'images' ? 'Images (JPG, PNG, GIF, WEBP)' : 
                                     field.allowedFileTypes === 'pdf' ? 'PDF files' : 
                                     field.allowedFileTypes === 'documents' ? 'Documents (PDF, DOC, DOCX)' : 
                                     field.allowedFileTypes}
                            {field.maxFileSize && ` | Max size: ${field.maxFileSize}MB`}
                            {field.allowMultiple && ' | Multiple files allowed'}
                        </Typography>
                    )}

                    <Button variant="outlined" component="label" disabled>
                        Upload File{field.allowMultiple ? 's' : ''} (Preview)
                        <input 
                            type="file" 
                            hidden 
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    onUpdate(field.id, { 
                                        fileName: file.name,
                                        fileSize: file.size,
                                        fileType: file.type
                                    });
                                }
                            }}
                        />
                    </Button>
                    {field.fileName && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Selected: {field.fileName} ({formatFileSize(field.fileSize)})
                        </Typography>
                    )}
                </Box>
            );
        
        case 'textarea':
            return (
                <TextField
                    label={field.label}
                    multiline
                    rows={4}
                    required={field.required}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                />
            );
        
        case 'date':
            return (
                <TextField
                    label={field.label || "Date"}
                    type="date"
                    value={field.value || ''}
                    onChange={(e) => onUpdate(field.id, { value: e.target.value })}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    InputLabelProps={{
                        shrink: true,                    }}
                    slotProps={{
                        input: {
                            shrink: true,
                        },
                    }}
                />
            );

        case 'programme-selector':
            return (
                <Box margin="normal" sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                        {field.label}
                        {field.required && <span style={{ color: 'red' }}> *</span>}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Programme Selection (Preview Mode)
                    </Typography>
                    
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Mode: {field.selectionMode === 'radio' ? 'Single Selection' : 'Multiple Selection'} |
                        Level: {field.programmeLevel || 'All Levels'} |
                        Faculty: {field.facultyFilter || 'All Faculties'} |
                        Display: {field.displayStyle || 'Hierarchical'}
                        {field.maxSelections && field.selectionMode !== 'radio' && ` | Max: ${field.maxSelections}`}
                    </Typography>
                    
                    <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            📚 Programme options will be dynamically loaded from the database based on your configuration.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Example: Faculty of Health Sciences → Department of Nursing → Bachelor of Nursing
                        </Typography>
                    </Box>
                </Box>
            );
        
        default:
            return (
                <TextField
                    label={field.label}
                    required={field.required}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                />
            );
    }
}